import { ClientField } from '../types/ClientField';
import { ContactField, ContactFieldValue } from '../types/ContactField';
import { ContactListFilterItem } from '../types/ContactListFilterItem';
import { ConversationGridFiltersItem } from '../types/ConversationGridFiltersItem';
import {
    ExtendsContactId,
    ExtendsContactIdentifiers,
    ExtendsCreateContactField,
    ExtendsFieldId,
    ExtendsLabel,
    ExtendsLeadId,
    ExtendsNoteId,
    ExtendsPlanId,
} from '../types/generics';
import { ViewProperty, ViewPropertyExtended } from '../types/ViewProperty';
import { hasMatchingFieldId } from './functionalFilters';
import { matchFieldTypeToDefaultGridFilter } from './functionalMatch';
// import ApScheduledStatus from "../types/Enums/ApScheduledStatus";
import { DateTime } from 'luxon';
import { ApScheduledStatus } from '../enums/ApScheduledStatus';
import { FieldDataType } from '../enums/FieldDataType';
import { FieldValidationType } from '../enums/FieldValidationType';
import { ApSchedulerContact } from '../types/ApSchedulerContact';
import { CalendarEvent } from '../types/CalendarEvent';
import { sanitizeAllTags } from './sanitizeHtml';

/**
 * mapToListOfFieldIds
 *
 * @description
 * Returns fieldId property from ExtendsFieldId object
 */
export const mapToListOfFieldIds = <T extends ExtendsFieldId>(field: T) => field.fieldId;

/**
 * mapToListOfFieldLabels
 *
 * @description
 * Returns label property from ExtendsLabel object
 */
export const mapToListOfFieldLabels = <T extends ExtendsLabel>(field: T) => field.label;

/**
 * mapToListOfLeadIds
 *
 * @description
 * Returns leadId property from ExtendsLeadId object
 */
export const mapToListOfLeadIds = <T extends ExtendsLeadId>(contact: T) => contact.leadId;

/**
 * mapToListOfContactIds
 *
 * @description
 * Returns contactId property from ExtendsContactId object
 */
export const mapToListOfContactIds = <T extends ExtendsContactId>(contact: T) => contact.contactId;

/**
 * mapToListOfPlanIds
 *
 * @description
 * Returns planId property from ExtendsPlanId object
 */
export const mapToListOfPlanIds = <T extends ExtendsPlanId>(plan: T) => plan.planId;

/**
 * mapReplaceIfMatchedFieldId
 *
 * @description
 * Returns the nextField argument if both objects have matching fieldId
 */
export const mapReplaceIfMatchedFieldId =
    <T extends ExtendsFieldId>(nextField: T) =>
    (checkedField: T): T =>
        nextField.fieldId === checkedField.fieldId ? nextField : checkedField;

/**
 * mapReplaceIfMatchedNoteId
 *
 * @description
 * Returns the nextNote argument if both objects have matching noteId
 */
export const mapReplaceIfMatchedNoteId =
    <T extends ExtendsNoteId>(nextNote: T) =>
    (checkedNote: T): T =>
        nextNote.noteId === checkedNote.noteId ? nextNote : checkedNote;

/**
 * mapContactFieldToClientField
 *
 * @description
 * Maps properties from a client field object into a contact field object
 */
export const mapClientFieldToContactField =
    <
        T extends Pick<ContactField, 'leadId' | 'rowId'> & {
            value?: ContactFieldValue;
        },
    >(
        contactField: T,
    ) =>
    (clientField: ClientField): ContactField => ({
        label: clientField.label,
        fieldType: clientField.fieldType,
        validationType: clientField.validationType,
        isPrimary: clientField.isPrimary,
        isUnique: clientField.isUnique,
        isFilterable: clientField.isFilterable,
        leadId: contactField.leadId,
        fieldId: clientField.fieldId,
        rowId: contactField.rowId,
        isVerifiable: clientField.isVerifiable,
        isVerified: clientField.isVerified,
        isReadOnly: clientField.isReadOnly,
        value:
            contactField.value === undefined
                ? clientField.validationType === FieldValidationType.address
                    ? {
                          street: '',
                          city: '',
                          province: '',
                          country: '',
                          postalCode: '',
                      }
                    : ''
                : contactField.value,
    });

/**
 * mapContactFieldsToView
 *
 * @description
 * Filters and sorts contact fields to match a view's property list, inserts 'empty'
 * if no match between view's properties and contact's fields are found
 */
export const mapContactFieldsToView =
    (contactFields: ContactField[]) =>
    (prop: ViewProperty): ContactField =>
        contactFields.filter(hasMatchingFieldId(prop.fieldId))[0] || ({} as ContactField);

/**
 * mapViewPropertiesToFields
 *
 * @description
 * Adds properties from matching client field to a view property object with matching fieldId
 */
export const mapViewPropertiesToField =
    (field: ClientField | undefined) =>
    (property: ViewProperty): ViewPropertyExtended => {
        const { label, isPrimary, validationType } = field || {
            label: '',
            isPrimary: false,
            validationType: FieldValidationType.none,
        };

        return {
            ...property,
            ...{
                label,
                isPrimary,
                validationType,
            },
        };
    };

/**
 * mapGridFilterToQueryFilter
 *
 * @description
 * Maps filter group object from ConversationGridFilters component to object accepted by API
 */
export const mapGridFilterToQueryFilter = (
    gridFilterItem: ConversationGridFiltersItem,
): ContactListFilterItem => ({
    fieldId: gridFilterItem.fieldId,
    fieldType: gridFilterItem.fieldType,
    operand: gridFilterItem.operand,
    value: gridFilterItem.value.toString(),
    value2: gridFilterItem.value2.toString(),
    values: [],
    isMultiselect: false,
});

/**
 * mapQueryFilterToGridFilter
 *
 * @description
 * Maps filter returned from query to an object usable by ConversationGridFilters component; values from
 * a matched ClientField object are required to add a label.
 */
export const mapQueryFilterToGridFilter =
    (queryFilter: ContactListFilterItem) =>
    (clientField: ClientField): ConversationGridFiltersItem => ({
        ...queryFilter,
        ...{
            fieldId: queryFilter.fieldId,
            fieldType: queryFilter.fieldType,
            fieldName: clientField?.label,
            value:
                queryFilter.fieldType === FieldDataType.float ||
                queryFilter.fieldType === FieldDataType.integer
                    ? +queryFilter.value
                    : queryFilter.value,
            value2: queryFilter.value2 !== undefined ? queryFilter.value2 : '',
            values: queryFilter.values,
            isMultiselect:
                queryFilter.fieldType === 6 ||
                queryFilter.fieldType === 7 ||
                queryFilter.fieldId === 16
                    ? true
                    : false,
        },
    });

/**
 * mapFieldToGridFilter
 *
 * @description
 * Maps the relevant values from a client field into an object which contains the default values for
 * creating a ConversationGridFiltersItem object.
 */
export const mapFieldToGridFilter = (field: ClientField): ConversationGridFiltersItem => ({
    fieldId: field.fieldId,
    fieldType: field.fieldType,
    fieldName: field.label,
    isMultiselect:
        field.fieldType === 6 || field.fieldType === 7 || field.fieldId === 16 ? true : false,
    ...matchFieldTypeToDefaultGridFilter(field),
});

/**
 * mapContactFormFieldToQueryField
 *
 * @description
 * Maps a contact field form object into an object with values expected when requesting to
 * create a new Contact
 */
export const mapFormFieldToCreateContactField = <T extends ExtendsCreateContactField>(
    field: T,
): {
    fieldId: number;
    value: ContactFieldValue;
} => ({
    fieldId: field.fieldId,
    value: field.value as ContactFieldValue,
});

// /**
//  * mapContactToApSchedulerContact
//  *
//  * @description
//  * Maps ExtendsContactIdentifiers object to ApSchedulerContact object with default values.
//  *
//  * Not for use if the ExtendsContactIdentifiers object has state set from Action Plan Scheduler
//  */
// export const mapContactToApSchedulerContact = <T extends ExtendsContactIdentifiers>(contact: T): ApSchedulerContact => ({
//     contactId: contact.leadId,
//     name: `${contact.firstname} ${contact.lastname}`.trim(),
//     sendStatus: ApScheduledStatus.notScheduled,
//     actionPlan: undefined
// });

/**
 * mapToSanitizedAllString
 *
 * @description
 * Removes all HTML tags from the given string
 */
export const mapToSanitizedAllString = (value: string) => sanitizeAllTags(value);

/**
 * mapStartEndDateToJsDate
 *
 * @description
 * Convert ISO 8061 date strings to DateTime objects
 */
export const mapStartEndDateToJsDate = (obj: CalendarEvent<string>): CalendarEvent<DateTime> => ({
    ...obj,
    ...{
        startDate: DateTime.fromISO(obj.startDate),
        endDate: obj.endDate ? DateTime.fromISO(obj.endDate as string) : null,
    },
});

/**
 * mapContactToApSchedulerContact
 *
 * @description
 * Maps ExtendsContactIdentifiers object to ApSchedulerContact object with default values.
 *
 * Not for use if the ExtendsContactIdentifiers object has state set from Action Plan Scheduler
 */
export const mapContactToApSchedulerContact = <T extends ExtendsContactIdentifiers>(
    contact: T,
): ApSchedulerContact => ({
    contactId: contact.leadId,
    name: `${contact.firstName} ${contact.lastName}`.trim(),
    sendStatus: ApScheduledStatus.notScheduled,
    actionPlan: undefined,
});
